import { FormEvent, useContext, useState, useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import { useMutation } from "react-query";

import Spinner from "components/spinner";
import { VendorContext } from "contexts/VendorContext";
import { VendorItemDetails } from "types/vendor";
import { getVendorItemDetails } from "api/vendorsApi";
import { logVendorItemDetailsPriceTrace } from "utils/inventoryPriceTrace";
import { getInventoryCategories } from "api/inventoryApi";
import { TInventoryCategory } from "types/inventory";
import VendorItemDetail from "./VendorItemDetail/VendorItemDetail";

import styles from "./VendorItems.module.scss";
import { SearchInput } from "components/input";
import { Button } from "components/button";
import { useModalContext } from "components/modal";
import VendorItemLinkModalBody from "./VendorItemLinkModal/VendorItemLinkModalBody";
import { useTranslation } from "react-i18next";
import VendorRemoteModal from "./VendorRemoteModal";
import axios from "axios";
import { useAuthContext } from "contexts/auth";

const VendorItems = () => {
  const { t } = useTranslation();
  const tBase = "views.vendors.VendorBody.VendorItems.VendorItems";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { setModal } = useModalContext();
  const { currentVendor, hideVendorDetails, vendorDetailsHidden } = useContext(VendorContext);
  const [updateUpdatedAt, setUpdateUpdatedAt] = useState<string | null>(null);
  const [updateUpdatedBy, setUpdateUpdatedBy] = useState<string | null>(null);
  const [updateInProgress, setUpdateInProgress] = useState<boolean>(false);
  const auth = useAuthContext();

  const [search, setSearch] = useState<string>("");

  const getUpdateVendorPricingStatus = useMutation(
    async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/siteone-vendor-update/update-prices/getSiteoneUpdatePricesStatus`,
        {
          params: {
            companyId: auth?.company_id,
          },
        }
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        setUpdateUpdatedBy(data.mostRecentCompletedLog?.full_name);
        setUpdateUpdatedAt(data.mostRecentCompletedLog?.updated_at);
        // setUpdateInProgress(!data.mostRecentLog?.is_completed);
      },
      onError: (error) => {
        console.error("Failed to fetch update status", error);
      },
    }
  );


  useEffect(() => {
    getUpdateVendorPricingStatus.mutate();
  }, []);

  useEffect(() => {
    setSearch("");
  }, [currentVendor?.vendor_id]);

  const vendorItemDetailsQuery = useQuery(
    ["vendors_items", currentVendor?.vendor_id],
    () => getVendorItemDetails(currentVendor?.vendor_id || null)
  );

  useEffect(() => {
    if (vendorItemDetailsQuery.data) {
      logVendorItemDetailsPriceTrace(
        currentVendor?.vendor_id,
        vendorItemDetailsQuery.data
      );
    }
  }, [vendorItemDetailsQuery.data, currentVendor?.vendor_id]);

  const categoryQuery = useQuery(["categories"], getInventoryCategories);

  const filteredVendorItems = useMemo(() => {
    const items: VendorItemDetails[] = vendorItemDetailsQuery.data ?? [];
    const query = search.trim().toLowerCase();
    if (!query) return items;

    const categoryNameById = new Map<string, string>(
      (categoryQuery.data ?? []).map((category: TInventoryCategory) => [
        String(category.category_id),
        String(category.category_name).toLowerCase(),
      ])
    );

    return items.filter((item) => {
      const categoryName =
        item.category_id != null
          ? categoryNameById.get(String(item.category_id)) ?? ""
          : "";

      const haystack = [
        item.item_name,
        item.model,
        item.sku,
        item.vendor_part_number,
        item.siteone_item_number,
        item.long_description,
        item.short_description,
        categoryName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [vendorItemDetailsQuery.data, search, categoryQuery.data]);

  const handleChangeSearch = (e: FormEvent<HTMLInputElement>) => {
    setSearch((e.target as HTMLInputElement).value);
  };

  if (vendorItemDetailsQuery?.isLoading)
    return (
      <div className={styles.container}>
        <Spinner />
      </div>
    );

  if (vendorItemDetailsQuery?.isError) return <div>{tr("Error")}</div>;

  const SITEONE_DEV_CLIENT_ID = "0oa21e94cnlLcZl2M0h8";
  const SITEONE_PROD_CLIENT_ID = "0oa10os6iflNmCJup2p8";

  const handleUpdatePrices = async () => {
    // SITEONE_DEV_CLIENT_ID;
    // dev authorization endpoint
    // const clientId = SITEONE_DEV_CLIENT_ID;
    // const redirectUri = `${process.env.REACT_APP_SERVER_URL}/api/v1/company/siteone-vendor-update/siteone-authentication-callback`;
    // const authorizationEndpoint = "https://siteone.oktapreview.com/oauth2/aus10m6iotqiJ1QTR0h8/v1/authorize";

    // production authorization endpoint
    const clientId = SITEONE_PROD_CLIENT_ID;
    const redirectUri = `https://api.eserv.com/api/v1/company/siteone-vendor-update/siteone-authentication-callback`;
    const authorizationEndpoint = "https://siteone.okta.com/oauth2/ausc43x2a6OlZmDDH2p7/v1/authorize";

    const token = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/siteone-vendor-update/update-prices/generate-token`, {
      // add a header to the request
      headers: {
        'siteone-authorization-token': '189273u4t890j238f02j348f28340j9va',
        'Content-Type': 'application/json',
      },
    });

    const stateData = {
      token: token.data.token,
      companyId: auth?.company_id,
      userId: auth?.user_id,
      vendorId: currentVendor?.vendor_id,
    };



    const params = new URLSearchParams({
      client_id: clientId,
      response_type: "code",
      // scope: "openid profile",
      scope: 'openid offline_access',
      redirect_uri: redirectUri,
      // state: "ttst:'dot'",
      // convert to base 64
      state: btoa(JSON.stringify(stateData)),
    });

    window.location.href = `${authorizationEndpoint}?${params.toString()}`;


    // Postman request
    // const stateData = {
    //   token: token.data.token,
    //   companyId: auth?.company_id,
    //   userId: auth?.user_id,
    //   vendorId: currentVendor?.vendor_id,
    // };

    // const params = {
    //   client_id: clientId,
    //   response_type: 'code',
    //   scope: 'openid offline_access',
    //   redirect_uri: redirectUri,
    //   state: JSON.stringify(stateData),
    // };

    // let config = {
    //   method: 'get',
    //   maxBodyLength: Infinity,
    //   url: authorizationEndpoint,
    //   headers: { },
    //   params,
    // } as any;

    // // Make the request
    // axios.request(config)
    //   .then((response) => {
    //     
    //   })
    //   .catch((error) => {
    //    
    //   });


  };

  // create update prices confirm modal

  const updatePricesModal = () => {
    setModal({
      component: (
        <div>
          <div style={{ margin: "10px 0 20px 0" }}>
            <p>{tr("Are you sure you want to update prices?")}</p>
            <p
              style={{
                fontSize: "14px",
                color: "#FF0000",
                fontStyle: "italic",
                marginBottom: "10px",
              }}
            >
              {tr("This action will take at least one day to complete.")}
            </p>
            <p>{tr("Last Updated By")}:
              <p style={{ float: "right" }}>
                {updateUpdatedBy || "N/A"}
              </p>
            </p>
            <p>{tr("Last Updated At")}:
              <p style={{ float: "right" }}>
                {/* convert to local time */}
                {updateUpdatedAt ? new Date(updateUpdatedAt).toLocaleString() : "N/A"}
              </p>
            </p>
          </div>
          {/* flex start end */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <Button primary onClick={handleUpdatePrices}>
              {tr("Confirm")}
            </Button>
            <Button onClick={() => setModal()}>
              {tr("Cancel")}
            </Button>
          </div>
        </div>
      ),
      label: tr("Update Prices"),
    });
  };

  const isIntegratedVendor = Boolean(currentVendor?.is_siteone || currentVendor?.is_heritage);

  const handleClickLinkItem = () => {
    setModal({
      component: (
        <VendorItemLinkModalBody vendorId={currentVendor?.vendor_id} isSiteoneVendor={currentVendor?.is_siteone} isHeritageVendor={currentVendor?.is_heritage} />
      ),
      label: isIntegratedVendor ? tr("Link Item") : tr("Add Item"),
      props: { fullScreen: true },
    });
  };

  const handleOpenRemote = () => {
    const vendorId = currentVendor?.vendor_id;
    if (vendorId) {
      setModal({
        component: <VendorRemoteModal vendorId={vendorId} />,
        label: "Refresh Prices",
      });
    } else {
      console.error("Vendor ID is not defined");
    }
  };

  return (
    <div className={styles.container} style={{ borderLeft: "none" }}>
      <div className={styles.topRow}>
        <div className={styles.headerLeft}>
          <div className={styles.title}>{tr("Items")}</div>
          <Button size="small" onClick={hideVendorDetails}>
            {vendorDetailsHidden ? tr("Collapse") : tr("Expand")}
          </Button>
        </div>

        <div className={styles.searchWrap}>
          <SearchInput
            search
            placeholder={tr("Search items")}
            className={styles.searchInput}
            value={search}
            onChange={handleChangeSearch}
          />
        </div>

        <div className={styles.actions}>
          {/* only show the update prices button if the company is 1, 14, 93, or the user (Jeff Hewitt of Sprinkler Solutions of Florida) is 617 | 121 = Timmy PooPoo Man*/}
          {[1, 14, 93].includes(Number(auth?.company_id)) || [617, 255, 121, 407].includes(Number(auth?.user_id)) ? (
            currentVendor?.is_siteone && (
              <Button
                size="small"
                onClick={updatePricesModal}
                disabled={updateInProgress}
                primary={currentVendor?.is_siteone_verified === false}
              >
                {
                  updateInProgress
                    ? tr("Updating SiteOne Prices...")
                    : tr("Update SiteOne Item Prices")
                }
              </Button>
            )
          ) : null}
          {/* <Button size="small" onClick={handleOpenRemote} className="mr-2">
            Pricing
          </Button> */}
          <Button
            size="small"
            onClick={() => {
              void vendorItemDetailsQuery.refetch();
            }}
            disabled={vendorItemDetailsQuery.isFetching}
          >
            {vendorItemDetailsQuery.isFetching ? tr("Refreshing...") : tr("Refresh")}
          </Button>
          <Button size="small" onClick={handleClickLinkItem}>
            {isIntegratedVendor ? tr("Link") : tr("Add Item")}
          </Button>
        </div>
      </div>
      <div className={styles.listContainer}>
        {search.trim() && filteredVendorItems.length === 0 ? (
          <div className={styles.noResults}>{tr("No items found")}</div>
        ) : null}
        {filteredVendorItems.map((i: VendorItemDetails) => (
          <VendorItemDetail key={i.id} item={i} vendorId={currentVendor?.vendor_id} />
        ))}
      </div>
    </div>
  );
};

export default VendorItems;
